import type { ReactNode } from 'react';

/**
 * Shared gradient avatar — ID-hash based color rotation, optional status dot.
 * Used in customer/provider lists, top-customers analytics row, etc.
 */

const AVATAR_PALETTE: { fg: string; bgFrom: string; bgTo: string; border: string }[] = [
  { fg: '#3390ec', bgFrom: 'rgba(51,144,236,0.14)',  bgTo: 'rgba(51,144,236,0.32)',  border: 'rgba(51,144,236,0.22)'  },
  { fg: '#1f8b4c', bgFrom: 'rgba(31,139,76,0.14)',   bgTo: 'rgba(31,139,76,0.32)',   border: 'rgba(31,139,76,0.22)'   },
  { fg: '#fa8c16', bgFrom: 'rgba(250,140,22,0.14)',  bgTo: 'rgba(250,140,22,0.32)',  border: 'rgba(250,140,22,0.22)'  },
  { fg: '#9254de', bgFrom: 'rgba(146,84,222,0.14)',  bgTo: 'rgba(146,84,222,0.32)',  border: 'rgba(146,84,222,0.22)'  },
  { fg: '#eb2f96', bgFrom: 'rgba(235,47,150,0.14)',  bgTo: 'rgba(235,47,150,0.32)',  border: 'rgba(235,47,150,0.22)'  },
];

function pickPalette(id: number) {
  return AVATAR_PALETTE[Math.abs(id) % AVATAR_PALETTE.length];
}

interface Props {
  /** Numeric id used for deterministic color rotation. */
  id: number;
  /** Display initials (1-2 chars) — if not provided, fallback icon is shown. */
  initials?: string;
  /** Fallback icon shown when no initials. */
  fallbackIcon?: ReactNode;
  /** Avatar size in pixels. Default 42. */
  size?: number;
  /** Border radius. Default depends on size (12 for >=40, 10 for smaller). */
  radius?: number;
  /** Status dot color (e.g. green for active). Pass null/undefined to hide. */
  statusColor?: string | null;
  /** Whether avatar should appear dimmed (e.g. inactive item). */
  dimmed?: boolean;
}

export default function GradientAvatar({
  id,
  initials,
  fallbackIcon,
  size = 42,
  radius,
  statusColor,
  dimmed = false,
}: Props) {
  const palette = pickPalette(id);
  const effectiveRadius = radius ?? (size >= 40 ? 13 : 10);
  const fontSize = Math.max(11, Math.round(size * 0.42));
  const dotSize = Math.max(10, Math.round(size * 0.28));

  return (
    <div style={{ position: 'relative', flexShrink: 0, width: size, height: size }}>
      <div
        style={{
          width: size,
          height: size,
          borderRadius: effectiveRadius,
          background: dimmed
            ? 'var(--bl-muted-soft, #eef0f3)'
            : `linear-gradient(135deg, ${palette.bgFrom} 0%, ${palette.bgTo} 100%)`,
          border: dimmed
            ? '1px solid var(--bl-divider, #ebebeb)'
            : `1px solid ${palette.border}`,
          color: dimmed ? 'var(--bl-text-muted, #9ca3af)' : palette.fg,
          fontSize,
          fontWeight: 800,
          letterSpacing: -0.3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.18s ease, color 0.18s ease',
        }}
      >
        {initials ? initials.slice(0, 2).toUpperCase() : fallbackIcon}
      </div>

      {statusColor && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            background: statusColor,
            border: '2px solid var(--bl-surface, #fff)',
            boxShadow: `0 0 0 2px ${statusColor}1f`,
          }}
        />
      )}
    </div>
  );
}
